import { Skeleton } from "@/atoms"
import cn from "classnames"
import { useEffect, useState } from "react"

import {
  Button,
  Divider,
  Form,
  Input,
  message,
  Modal,
  Select,
  Text,
  Title
} from "@/atoms"
import { JiraFieldKey } from "@/const/jira.constants"
import { buildCreateIssuePayload } from "@/helpers/jira.helpers"
import { toDictionary } from "@/helpers/utils"
import { useTranslation } from "@/hooks/useTranslation"
import { JiraSoftwareLogo, WarningOutlined } from "@/icons"
import { RichTextEditor } from "@/molecules/RichTextEditor"
import {
  FormError,
  JiraIssueErrorResponse
} from "@/organisms/Ticketing/components/JiraIssueModal/JiraIssueModal.types"
import { useTicketingMetadata } from "@/organisms/Ticketing/hooks/useTicketingMetadata"

import styles from "./JiraIssueModal.module.scss"

import type { JiraIssueFormValues, JiraIssueModalProps } from "."
import { JiraIssueModalFields } from "."

export const JiraIssueModal = ({
  jira,
  type,
  closeModal,
  onCreateJiraIssue,
  createJiraIssueMutation
}: JiraIssueModalProps) => {
  const jiraKeyPrefix = "jiraIssueModal"
  const { t } = useTranslation("translation", {
    keyPrefix: jiraKeyPrefix
  })
  const { t: tGeneral } = useTranslation("translation", {
    keyPrefix: "general"
  })

  const [creating, setCreating] = useState(false)
  const [formError, setFormError] = useState<FormError | null>(null)

  const [initialDescription, setInitialDescription] = useState<null | string>(
    null
  )
  const [form] = Form.useForm<JiraIssueFormValues>()

  // Use shared hook for project/issue type management
  const {
    projectId,
    issueTypeId,
    projects,
    issueTypesOptions,
    fields,
    fieldsMetadata,
    metadataIsLoading: isLoading,
    fieldsIsLoading,
    url,
    handleProjectChange: baseHandleProjectChange
  } = useTicketingMetadata(form)

  const handleCloseModal = (
    fieldsValues?: JiraIssueFormValues,
    fieldsMetadata?: Record<string, Jira.IssueTypeField>
  ) => {
    setInitialDescription(null)
    setFormError(null)
    closeModal(fieldsValues, fieldsMetadata)
  }

  useEffect(() => {
    if (!isLoading && projects.length > 0) {
      const { savedProjectId, savedIssueTypeId } = getSavedProjectAndIssueType()
      const projectToSet =
        jira?.project ||
        (savedProjectId && projects.some((p) => p.id === savedProjectId)
          ? savedProjectId
          : projects[0].id)
      const selectedProject = projects.find((p) => p.id === projectToSet)
      const issueTypeToSet =
        savedIssueTypeId &&
        selectedProject?.issuetypes.some((it) => it.id === savedIssueTypeId)
          ? savedIssueTypeId
          : selectedProject?.issuetypes[0]?.id

      form.setFieldsValue({
        [JiraFieldKey.Project]: projectToSet,
        [JiraFieldKey.IssueType]: issueTypeToSet
      })
    }
  }, [isLoading, projects, form])

  const getSavedProjectAndIssueType = (): {
    savedProjectId: string | null
    savedIssueTypeId: string | null
  } => {
    return {
      savedProjectId: localStorage.getItem("selectedJiraProject"),
      savedIssueTypeId: localStorage.getItem("selectedJiraIssueType")
    }
  }

  const saveProjectAndIssueType = (projectId: string, issueTypeId: string) => {
    localStorage.setItem("selectedJiraProject", projectId)
    localStorage.setItem("selectedJiraIssueType", issueTypeId)
  }

  const { isError } = createJiraIssueMutation || {}

  const errorProcessing = (
    isError: boolean | undefined,
    error: JiraIssueErrorResponse
  ) => {
    try {
      if (isError) {
        const { data } = error || {}
        const { detail } = data || {}
        const {
          errorCode,
          userMessages = [],
          fields: errorFields = {}
        } = detail || {}

        const formErrors: string[] = []
        const fieldErrorsToSet: Array<{ name: string; errors: string[] }> = []

        // Handle field validation errors (400)
        if (errorCode === "fieldError" && Object.keys(errorFields).length > 0) {
          const fieldsNames = toDictionary(fields, "key")
          const currentUiFields = form.getFieldsValue()

          // Add form-level error message if userMessages exist
          if (userMessages.length > 0) {
            formErrors.push(...userMessages)
          }

          // Collect field-specific errors
          Object.entries(errorFields).forEach(([fieldKey, errorMessage]) => {
            const fieldMetadata = fieldsNames[fieldKey]

            if (fieldMetadata) {
              if (!(fieldMetadata.key in currentUiFields)) {
                formErrors.push(
                  t("error.notSupportedRequiredField", {
                    fieldName: fieldMetadata.name
                  })
                )
              } else {
                fieldErrorsToSet.push({
                  name: fieldMetadata.key,
                  errors: [errorMessage]
                })
              }
            }
          })
        }

        // Handle not found errors (404)
        else if (errorCode === "notFoundError") {
          formErrors.push(userMessages[0] || t("error.resourceNotFound"))
        }
        // Handle general Jira errors (500)
        else if (errorCode === "JiraError") {
          formErrors.push(userMessages[0] || t("error.creationFailed"))
        } else if (userMessages.length > 0) {
          formErrors.push(...userMessages)
        } else {
          formErrors.push("creationFailed")
        }

        // Batch update all state changes at once
        if (fieldErrorsToSet.length > 0) {
          form.setFields(fieldErrorsToSet)
        }
        if (formErrors.length > 0) {
          setFormError(formErrors.join("; "))
        }

        return
      }
    } catch {
      setFormError("creationFailed")
    }
  }

  useEffect(() => {
    // only for remedies
    errorProcessing(isError, createJiraIssueMutation?.error)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError])

  const onFieldsChange = () => {
    if (formError === "completeRequiredFields") {
      const fieldErrors = form.getFieldsError()
      const someFieldsAreRequired = fieldErrors.some((f) =>
        f.errors.some((e) => e === "Required field")
      )
      if (!someFieldsAreRequired) {
        setFormError(null)
      }
    }
  }

  useEffect(() => {
    setFormError(null)
  }, [issueTypeId, projectId])

  useEffect(() => {
    if (jira) {
      const descriptionValue = jira?.description ?? ""
      const { savedProjectId, savedIssueTypeId } = getSavedProjectAndIssueType()
      const projectToSet =
        savedProjectId && projects.some((p) => p.id === savedProjectId)
          ? savedProjectId
          : projects[0]?.id
      const selectedProject = projects.find((p) => p.id === projectToSet)
      const issueTypeToSet =
        savedIssueTypeId &&
        selectedProject?.issuetypes.some((it) => it.id === savedIssueTypeId)
          ? savedIssueTypeId
          : selectedProject?.issuetypes[0]?.id

      setInitialDescription(descriptionValue)

      form.setFieldsValue({
        [JiraFieldKey.Project]: projectToSet,
        [JiraFieldKey.IssueType]: issueTypeToSet,
        [JiraFieldKey.Summary]: jira?.summary ?? "",
        [JiraFieldKey.Description]: descriptionValue
      })
    } else {
      form.resetFields([
        JiraFieldKey.Summary,
        JiraFieldKey.Project,
        JiraFieldKey.IssueType,
        JiraFieldKey.Description
      ])
    }
  }, [jira, form, projects])

  const errorTranslationKey = `error.${formError}`
  const errorTranslationResult = t(errorTranslationKey)
  const errorMessage =
    errorTranslationResult === `${jiraKeyPrefix}.${errorTranslationKey}`
      ? formError
      : errorTranslationResult

  const [messageApi, messageContext] = message.useMessage()

  const createButtonDisabled = fieldsIsLoading || creating
  const LoadingComponent = isLoading && (
    <Skeleton active paragraph={{ rows: 6 }} />
  )

  return (
    <>
      <Modal
        destroyOnHidden
        zIndex={1201}
        maskClosable={false}
        className={cn(
          styles.modal,
          styles.modalIssueTextEditor,
          isLoading && styles.isLoading
        )}
        open={type === "jira"}
        width={650}
        onCancel={() => handleCloseModal()}
        afterOpenChange={(isOpen) => {
          if (!isOpen && onCreateJiraIssue) {
            form.resetFields()
          }
        }}
        title={
          <div className={styles.header}>
            <JiraSoftwareLogo className={styles.icon} />
            <div className={styles.textSection}>
              <Title level={5}>{t("title")}</Title>
              {!isLoading && (
                <Text type="secondary">
                  {`${t("description")}`} <a href={url}>{url}</a>
                </Text>
              )}
            </div>
          </div>
        }
        footer={[
          <div key="error" className={styles.formErrorContainer}>
            {formError ? (
              <>
                <WarningOutlined className={styles.formErrorIcon} />
                <Text className={styles.errorMessage}>{errorMessage}</Text>
              </>
            ) : null}
          </div>,
          <Button key="cancel" onClick={() => handleCloseModal()}>
            {tGeneral("cancel")}
          </Button>,
          <Button
            key="submit"
            htmlType="submit"
            type="primary"
            disabled={createButtonDisabled}
            loading={creating}
            onClick={async () => {
              const fieldsValues = form.getFieldsValue()
              if (!onCreateJiraIssue)
                return handleCloseModal(fieldsValues, fieldsMetadata)

              setCreating(true)

              try {
                const jiraIssue = buildCreateIssuePayload({
                  fieldsMetadata,
                  fieldsValues
                })

                // For regular mode, handle response and show success message
                const response = await onCreateJiraIssue(jiraIssue, jira)

                if (!response) return

                const { data } = response
                const { name, url } = data

                const ClickableMessage: React.FC<{
                  name: string
                  url: string
                }> = ({ name, url }) => (
                  <span>
                    Jira ticket-{" "}
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        cursor: "pointer",
                        textDecoration: "underline",
                        color: "inherit"
                      }}
                    >
                      {name}
                    </a>{" "}
                    opened successfully
                  </span>
                )

                handleCloseModal()

                messageApi.open({
                  type: "success",
                  content: <ClickableMessage name={name} url={url} />,
                  duration: 10
                })
              } catch (error: any) {
                errorProcessing(true, error)
              } finally {
                setCreating(false)
              }
            }}
          >
            {tGeneral("create")}
          </Button>
        ]}
      >
        {LoadingComponent}
        {!isLoading && (
          <Form
            onFieldsChange={onFieldsChange}
            className={styles.form}
            layout="vertical"
            form={form}
          >
            <div className={styles.itemForm}>
              <Form.Item
                className={styles.selectSection}
                name={JiraFieldKey.Project}
                label={
                  <Text className={styles.label}>{t("projectField")}</Text>
                }
              >
                <Select
                  fieldNames={{ value: "id", label: "name" }}
                  options={projects}
                  filterOption={(input, option) => {
                    if (!option) return false

                    return (option.name || "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }}
                  showSearch
                  onChange={(value: string) => {
                    const selectedProject = projects.find((p) => p.id === value)
                    const issueTypeId = selectedProject?.issuetypes[0]?.id
                    // JiraIssueModal-specific: save to localStorage
                    saveProjectAndIssueType(value, issueTypeId || "")
                    // Update form using hook's handler
                    baseHandleProjectChange(value)
                  }}
                />
              </Form.Item>
            </div>
            <div className={styles.itemForm}>
              <Form.Item
                className={styles.selectSection}
                name={JiraFieldKey.IssueType}
                label={
                  <Text className={styles.label}>{t("issueTypeField")}</Text>
                }
              >
                <Select
                  fieldNames={{ value: "id", label: "name" }}
                  options={issueTypesOptions}
                  onChange={(value: string) => {
                    const projectId = form.getFieldValue(JiraFieldKey.Project)
                    saveProjectAndIssueType(projectId, value)
                  }}
                />
              </Form.Item>
            </div>
            <Divider className={styles.divider} />
            <Form.Item
              className={styles.itemForm}
              label={<Text className={styles.label}>{t("summaryField")}</Text>}
              rules={[{ required: true, message: t("error.summary") || "" }]}
              name={JiraFieldKey.Summary}
              initialValue={jira?.summary}
            >
              <Input />
            </Form.Item>

            <div className={styles.itemForm}>
              <Form.Item
                name={JiraFieldKey.Description}
                style={{ display: "none" }}
              />
              <Text className={styles.label}>{t("descriptionField")}</Text>
              {initialDescription === null ? null : (
                <RichTextEditor
                  html={initialDescription}
                  onChange={(value: string) => {
                    form.setFieldValue(JiraFieldKey.Description, value)
                  }}
                />
              )}
            </div>
            {!isLoading && (
              <JiraIssueModalFields
                projectId={projectId}
                issueTypeId={issueTypeId}
              />
            )}
          </Form>
        )}
      </Modal>
      {messageContext}
    </>
  )
}
